<script setup lang="ts">
import { ref, reactive, onUnmounted } from "vue";
import {
  SupportedEaseNameToCubicBezier,
  animationEnd$,
  animationStart$
} from "@zyzgroup/core-web";
import Box from "./Box.vue";

export type SupportedCSSAnimation =
  | "jelly"
  | "bounce"
  | "flash"
  | "pulse"
  | "rubberBand"
  | "shakeX"
  | "shakeY"
  | "headShake"
  | "swing"
  | "tada"
  | "wobble"
  | "jello"
  | "heartBeat"
  | "backInLeft"
  | "backInRight"
  | "backInUp"
  | "backInDown"
  | "backOutLeft"
  | "backOutRight"
  | "backOutUp"
  | "backOutDown"
  | "bounceIn"
  | "bounceInLeft"
  | "bounceInRight"
  | "bounceInUp"
  | "bounceInDown"
  | "bounceOut"
  | "bounceOutLeft"
  | "bounceOutRight"
  | "bounceOutUp"
  | "bounceOutDown"
  | "fadeIn"
  | "fadeInLeft"
  | "fadeInLeftBig"
  | "fadeInRight"
  | "fadeInRightBig"
  | "fadeInUp"
  | "fadeInUpBig"
  | "fadeInDown"
  | "fadeInDownBig"
  | "fadeInTopLeft"
  | "fadeInTopRight"
  | "fadeInBottomLeft"
  | "fadeInBottomRight"
  | "fadeOut"
  | "fadeOutLeft"
  | "fadeOutLeftBig"
  | "fadeOutRight"
  | "fadeOutRightBig"
  | "fadeOutUp"
  | "fadeOutUpBig"
  | "fadeOutDown"
  | "fadeOutDownBig"
  | "fadeOutTopLeft"
  | "fadeOutTopRight"
  | "fadeOutBottomRight"
  | "fadeOutBottomLeft"
  | "flip"
  | "flipInX"
  | "flipInY"
  | "flipOutX"
  | "flipOutY"
  | "flipOutRight"
  | "flipInLeft"
  | "flipOutLeft"
  | "flipInRight"
  | "flipOutTop"
  | "flipInBottom"
  | "flipOutBottom"
  | "flipInTop"
  | "lightSpeedInLeft"
  | "lightSpeedInRight"
  | "lightSpeedOutLeft"
  | "lightSpeedOutRight"
  | "rotateIn"
  | "rotateInUpLeft"
  | "rotateInUpRight"
  | "rotateInDownLeft"
  | "rotateInDownRight"
  | "rotateOut"
  | "rotateOutUpLeft"
  | "rotateOutUpRight"
  | "rotateOutDownLeft"
  | "rotateOutDownRight"
  | "hinge"
  | "rotateFall"
  | "rotateOutNewspaper"
  | "rotateInNewspaper"
  | "jackInTheBox"
  | "rollIn"
  | "rollOut"
  | "zoomIn"
  | "zoomInLeft"
  | "zoomInRight"
  | "zoomInUp"
  | "zoomInDown"
  | "zoomOut"
  | "zoomOutLeft"
  | "zoomOutRight"
  | "zoomOutUp"
  | "zoomOutDown"
  | "fade"
  | "moveFromLeft"
  | "moveToLeft"
  | "moveFromRight"
  | "moveToRight"
  | "moveFromTop"
  | "moveToTop"
  | "moveFromBottom"
  | "moveToBottom"
  | "moveFromLeftFade"
  | "moveToLeftFade"
  | "moveFromRightFade"
  | "moveToRightFade"
  | "moveFromTopFade"
  | "moveToTopFade"
  | "moveFromBottomFade"
  | "moveToBottomFade"
  | "scaleFromSmall"
  | "scaleToSmall"
  | "scaleFromLarge"
  | "scaleToLarge"
  | "scaleFromSmallCenter"
  | "scaleToSmallCenter"
  | "slideInLeft"
  | "slideInRight"
  | "slideInUp"
  | "slideInDown"
  | "slideOutLeft"
  | "slideOutRight"
  | "slideOutUp"
  | "slideOutDown"
  | "rotateRightSideFirst"
  | "rotateLeftSideFirst"
  | "rotateTopSideFirst"
  | "rotateBottomSideFirst"
  | "rotatePushLeft"
  | "rotatePushRight"
  | "rotatePushTop"
  | "rotatePushBottom"
  | "rotatePullRight"
  | "rotatePullLeft"
  | "rotatePullTop"
  | "rotatePullBottom"
  | "rotateFoldRight"
  | "rotateFoldLeft"
  | "rotateFoldTop"
  | "rotateFoldBottom"
  | "rotateUnfoldLeft"
  | "rotateUnfoldRight"
  | "rotateUnfoldTop"
  | "rotateUnfoldBottom"
  | "rotateRoomLeftOut"
  | "rotateRoomLeftIn"
  | "rotateRoomRightOut"
  | "rotateRoomRightIn"
  | "rotateRoomTopOut"
  | "rotateRoomTopIn"
  | "rotateRoomBottomOut"
  | "rotateRoomBottomIn"
  | "rotateCubeLeftOut"
  | "rotateCubeLeftIn"
  | "rotateCubeRightOut"
  | "rotateCubeRightIn"
  | "rotateCubeTopOut"
  | "rotateCubeTopIn"
  | "rotateCubeBottomOut"
  | "rotateCubeBottomIn"
  | "rotateCarouselLeftOut"
  | "rotateCarouselLeftIn"
  | "rotateCarouselRightOut"
  | "rotateCarouselRightIn"
  | "rotateCarouselTopOut"
  | "rotateCarouselTopIn"
  | "rotateCarouselBottomOut"
  | "rotateCarouselBottomIn"
  | "rotateSidesOut"
  | "rotateSidesIn"
  | "rotateSlideOut"
  | "rotateSlideIn"
  | "superRainbow"
  | "statusProcessing";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

// timingFunction :
// linear
// ease
// ease-in
// ease-out
// ease-in-out
// step-start=steps(1, start) 动画执行时以开始端点为开始
// step-end=steps(1, end) 动画执行时以结尾端点为开始、
// cubic-bezier()
// steps()

// animation: moveObject 2s ease-in-out, fadeBox 3s linear;
// animation-name: moveObject, fadeBox;
// animation-duation: 2s, 3s;
// animation-timing-function: ease-in-out, linear;

// .exmaple {
//   animation-play-state: paused;
// }
// .exmaple:hover {
//   animation-play-state: running;
// }

const props = withDefaults(
  defineProps<{
    name?: SupportedCSSAnimation;
    timingFunction?: SupportedEaseNameToCubicBezier;
    duration?: number;
    delay?: number;
    repeat?: number | "infinite";
    direction?: "normal" | "reverse" | "alternate" | "alternate-reverse";
    fillMode?: "none" | "forwards" | "backwards" | "both";
    playState?: "running" | "paused";
    perspective?: string;
  }>(),
  {
    name: "jelly",
    fillMode: "both",
    playState: "running",
    perspective: "1200px"
  }
);

const emits = defineEmits<{
  (event: "cssanimationstart", e: AnimationEvent): void;
  (event: "cssanimationend", e: AnimationEvent): void;
}>();

const animationRef = ref();

const style = reactive<Record<string, string>>({
  cursor: "pointer",
  transformStyle: "preserve-3d"
});

let duration = props.duration;
let timingFunction = props.timingFunction;
let delay = props.delay;
let repeat = props.repeat;
let direction = props.direction;

function changeEffect(animationName: SupportedCSSAnimation) {
  // duration
  switch (animationName) {
    case "superRainbow":
      duration = props.duration || 15000;
      break;
    case "heartBeat":
      duration = props.duration || 1300;
      break;
    case "statusProcessing":
      duration = props.duration || 1200;
      break;
    case "rotateFall":
    case "rotateSlideOut":
    case "rotateSlideIn":
      duration = props.duration || 1000;
      break;
    case "rotateRightSideFirst":
    case "rotateLeftSideFirst":
    case "rotateTopSideFirst":
    case "rotateBottomSideFirst":
    case "rotatePushLeft":
    case "rotatePushRight":
    case "rotatePushTop":
    case "rotatePushBottom":
    case "rotateRoomLeftOut":
    case "rotateRoomLeftIn":
    case "rotateRoomRightOut":
    case "rotateRoomRightIn":
    case "rotateRoomTopOut":
    case "rotateRoomTopIn":
    case "rotateRoomBottomOut":
    case "rotateRoomBottomIn":
    case "rotateCarouselLeftOut":
    case "rotateCarouselLeftIn":
    case "rotateCarouselRightOut":
    case "rotateCarouselRightIn":
    case "rotateCarouselTopOut":
    case "rotateCarouselTopIn":
    case "rotateCarouselBottomOut":
    case "rotateCarouselBottomIn":
      duration = props.duration || 800;
      break;
    case "bounceIn":
    case "bounceOut":
    case "flipOutX":
    case "hinge":
      duration = props.duration || 750;
      break;
    case "rotateFoldRight":
    case "rotateFoldLeft":
    case "rotateFoldTop":
    case "rotateFoldBottom":
    case "rotateUnfoldLeft":
    case "rotateUnfoldRight":
    case "rotateUnfoldTop":
    case "rotateUnfoldBottom":
    case "fade":
    case "moveFromLeftFade":
    case "moveToLeftFade":
    case "moveFromRightFade":
    case "moveToRightFade":
    case "moveFromTopFade":
    case "moveToTopFade":
    case "moveFromBottomFade":
    case "moveToBottomFade":
      duration = props.duration || 700;
      break;
    case "moveFromLeft":
    case "moveToLeft":
    case "moveFromRight":
    case "moveToRight":
    case "moveFromTop":
    case "moveToTop":
    case "moveFromBottom":
    case "moveToBottom":
    case "rotateCubeLeftOut":
    case "rotateCubeLeftIn":
    case "rotateCubeRightOut":
    case "rotateCubeRightIn":
    case "rotateCubeTopOut":
    case "rotateCubeTopIn":
    case "rotateCubeBottomOut":
    case "rotateCubeBottomIn":
      duration = props.duration || 600;
      break;
    default:
      duration = props.duration || 500;
      break;
  }

  // timingFunction
  switch (animationName) {
    case "superRainbow":
      timingFunction = props.timingFunction || "linear";
      break;
    case "lightSpeedOutLeft":
    case "lightSpeedOutRight":
    case "rotateRightSideFirst":
    case "rotateLeftSideFirst":
    case "rotateTopSideFirst":
    case "rotateBottomSideFirst":
    case "flipOutRight":
    case "flipOutLeft":
    case "flipOutTop":
    case "flipOutBottom":
    case "rotateFall":
    case "rotateOutNewspaper":
    case "rotateCubeLeftOut":
    case "rotateCubeLeftIn":
    case "rotateCubeRightOut":
    case "rotateCubeRightIn":
    case "rotateCubeTopOut":
    case "rotateCubeTopIn":
    case "rotateCubeBottomOut":
    case "rotateCubeBottomIn":
    case "rotateSidesOut":
      timingFunction = props.timingFunction || "ease-in";
      break;
    case "lightSpeedInLeft":
    case "lightSpeedInRight":
    case "flipInLeft":
    case "flipInRight":
    case "flipInBottom":
    case "flipInTop":
    case "rotateInNewspaper":
    case "rotateSidesIn":
      timingFunction = props.timingFunction || "ease-out";
      break;
    case "pulse":
    case "headShake":
    case "heartBeat":
    case "statusProcessing":
      timingFunction = props.timingFunction || "ease-in-out";
      break;
    default:
      timingFunction = props.timingFunction || "ease";
      break;
  }

  // delay
  switch (animationName) {
    default:
      delay = props.delay || 0;
      break;
  }

  // repeat
  switch (animationName) {
    case "superRainbow":
    case "statusProcessing":
      repeat = props.repeat || "infinite";
      break;
    default:
      repeat = props.repeat || 1;
      break;
  }

  // direction
  switch (animationName) {
    case "superRainbow":
      direction = props.direction || "alternate";
      break;
    default:
      direction = props.direction || "normal";
      break;
  }

  // transformOrigin: x-offset y-offset
  switch (animationName) {
    case "hinge":
    case "rotateFall":
      style.transformOrigin = "left top";
      // style.transformOrigin = "0% 0%";
      break;
    case "zoomOutLeft":
    case "rotateRightSideFirst":
    case "rotatePushLeft":
    case "rotatePullLeft":
    case "rotateFoldRight":
    case "rotateUnfoldRight":
    case "rotateRoomLeftIn":
    case "rotateRoomRightOut":
    case "rotateCubeLeftIn":
    case "rotateCubeRightOut":
      style.transformOrigin = "left center";
      // style.transformOrigin = "0% 50%";
      break;
    case "rotateInUpLeft":
    case "rotateInDownLeft":
    case "rotateOutUpLeft":
    case "rotateOutDownLeft":
      style.transformOrigin = "left bottom";
      // style.transformOrigin = "0% 100%";
      break;
    case "swing":
    case "zoomOutUp":
    case "rotateBottomSideFirst":
    case "rotatePushTop":
    case "rotatePullTop":
    case "rotateFoldBottom":
    case "rotateUnfoldBottom":
    case "rotateRoomTopIn":
    case "rotateRoomBottomOut":
    case "rotateCubeTopIn":
    case "rotateCubeBottomOut":
      style.transformOrigin = "center top";
      // style.transformOrigin = "50% 0%";
      break;
    case "bounce":
    case "zoomOutDown":
    case "rotateTopSideFirst":
    case "rotatePushBottom":
    case "rotatePullBottom":
    case "rotateFoldTop":
    case "rotateUnfoldTop":
    case "rotateRoomTopOut":
    case "rotateRoomBottomIn":
    case "rotateCubeTopOut":
    case "rotateCubeBottomIn":
      style.transformOrigin = "center bottom";
      // style.transformOrigin = "50% 100%";
      break;
    // case "xxx":
    //   style.transformOrigin = "right top";
    //   style.transformOrigin = "100% 0%";
    //   break;
    case "zoomOutRight":
    case "rotateLeftSideFirst":
    case "rotatePushRight":
    case "rotatePullRight":
    case "rotateFoldLeft":
    case "rotateUnfoldLeft":
    case "rotateRoomLeftOut":
    case "rotateRoomRightIn":
    case "rotateCubeLeftOut":
    case "rotateCubeRightIn":
      style.transformOrigin = "right center";
      // style.transformOrigin = "100% 50%";
      break;
    case "rotateInUpRight":
    case "rotateInDownRight":
    case "rotateOutUpRight":
    case "rotateOutDownRight":
      style.transformOrigin = "right bottom";
      // style.transformOrigin = "100% 100%";
      break;
    default:
      // style.transformOrigin = "center"; // default
      // style.transformOrigin = "50% 50%"; // default
      delete style.transformOrigin;
      break;
  }

  // overflow
  switch (animationName) {
    case "jelly":
    case "bounce":
    case "flash":
    case "pulse":
    case "rubberBand":
    case "shakeX":
    case "shakeY":
    case "headShake":
    case "swing":
    case "tada":
    case "wobble":
    case "jello":
    case "heartBeat":
    case "bounceIn":
    case "fadeIn":
    case "fadeOut":
    case "flip":
    case "flipInX":
    case "flipInY":
    case "flipOutX":
    case "flipOutY":
    case "hinge":
    case "jackInTheBox":
      // style.overflow = "visible";
      if (animationRef.value) {
        animationRef.value.getElement().parentElement.style.overflow =
          "visible";
      }
      break;
    default:
      // style.overflow = "hidden";
      if (animationRef.value) {
        animationRef.value.getElement().parentElement.style.overflow = "hidden";
      }
      break;
  }

  // backfaceVisibility
  switch (animationName) {
    case "flip":
    case "flipInX":
    case "flipInY":
    case "flipOutX":
    case "flipOutY":
      style.backfaceVisibility = "visible";
      break;
    default:
      delete style.backfaceVisibility;
      break;
  }
}

let s1;
let s2;
const mount = (box) => {
  s1 = animationStart$(box.el).subscribe((e) => {
    emits("cssanimationstart", e);
  });
  s2 = animationEnd$(box.el).subscribe((e) => {
    emits("cssanimationend", e);
  });
};

// 清除
const clear = () => {
  style.animation = "none";
};

const set = (animationName: SupportedCSSAnimation) => {
  if (animationRef.value) {
    // restart css animation
    const el = animationRef.value.getElement();
    el.style.animation = "none";
    el.offsetHeight; /* trigger reflow */
    el.style.animation = null;
  }
  // style.animationName = `${animationName}`;
  // style.animationDuration = `${duration}ms`;
  // style.animationDelay = `${delay}ms`;
  // style.animationIterationCount = `${repeat}`;
  // style.animationFillMode = `${props.fillMode}`;
  // style.animationTimingFunction = `${timingFunction}`;
  // style.animationDirection = `${direction}`;
  // style.animationPlayState = `${props.playState}`;
  style.animation = `${duration}ms ${timingFunction} ${delay}ms ${repeat} ${direction} ${props.fillMode} ${props.playState} ${animationName}`;
};

const trigger = (newAnimationName?: SupportedCSSAnimation) => {
  clear();
  changeEffect(newAnimationName || props.name);
  direction = "normal";
  set(newAnimationName || props.name);
};

const triggerReverse = (newAnimationName?: SupportedCSSAnimation) => {
  clear();
  changeEffect(newAnimationName || props.name);
  direction = "reverse";
  set(newAnimationName || props.name);
};

onUnmounted(() => {
  s1?.unsubscribe();
  s2?.unsubscribe();
});

defineExpose({ trigger, triggerReverse, clear });
</script>

<template>
  <Box
    ref="animationRef"
    class="css-animation"
    display-type="empty"
    :style="style"
    @mount="mount"
  >
    <slot></slot>
  </Box>
</template>

<style>
@import "../../../../../css/animate_ce.css";
</style>
